// 3 · Kitchen display - THE one kitchen screen: /dining/kds and the Kitchen PWA
// (apps/kds, kds.<domain>, /kds, /pwa/kds) both render it. The tickets of the ONE
// kitchen (GET /api/dining/kitchen/tickets, docs/DINING_API_2026-10-28.md §11):
// table, register, kiosk and online orders all arrive there. Every tap is a
// command on the ticket state machine and the board shows what the server
// answers; live from the restaurant event stream (§13), never a timer poll.
//
// THE OWNER'S LAYOUT (Oct 28 2026: "Redesign the KDS the same as the OLD design"):
// a header (logo, Kitchen Display, restaurant · station, live badge, clock and
// the sound / refresh / filter / fullscreen / settings icons), four big
// colour-coded counters and four COLOUR-CODED COLUMNS:
//   NEW (blue) -> COOKING (amber) -> READY (green) -> DONE (slate)
// A ticket is a card in its status column and wears the column's colour; ONE
// button moves it on: Start (new -> preparing), Ready (preparing -> ready), Done
// (ready -> served: pressed by the food preparer when the server picks it up).
// DONE IS THE HISTORY: the done tickets of the last N hours (served, and voided
// tickets the kitchen acknowledged), most recent first, compact, with when and
// who; Recall (tap, then confirm) puts one back in Ready. A ticket whose every
// item was voided after it was sent sits at the top of NEW in red until the
// kitchen taps Acknowledge.
//
// NO PAGE SCROLL ("no scroll is allowed, all must be on the same page"): the page
// is 100vh, overflow hidden; the header, counters and all four columns are
// always on screen. Each column fits its own height: its cards compact step by
// step (DENSITY), then the column pages ("+N more", turns every 10 s, arrows) -
// one column never pushes another. The counters are the columns' counts.
// Age: m:ss under 1 h, "1:05h" under 24 h, else "Nd"; on New / Cooking the age
// pill turns orange at 5 min and red at 10.
// A ticket older than the kitchen's stale window (12 h, backend
// dining/kitchen.py STALE_AFTER_HOURS) is not on the board; the header says how
// many were left off and a manager clears them (POST …/tickets/clear-old).
// Settings also lists the latest tickets with what the kitchen printer did with
// each (printed / printing / failed / no printer - the server's word, never a
// guess) and a Reprint per ticket (POST …/tickets/{id}/reprint, once per tap).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { STATUS } from '@/lib/diningEvents';
import { api, errorText, newKey } from '../lib/api';
import { clock, elapsed, isChannelTicket, modifierText, orderTypeLabel, parseTime, plural, shortName, splitNotes, ticketPlace } from '../lib/format';
import { useLive } from '../lib/useLive';
import { useDiningSession } from '../lib/session';
import { chime, useSoundPref } from '../lib/sound';
import { C, courseLabel, FONT_TIGHT } from '../theme/tokens';
import { UserMenu } from '../components/Shell';
import { Clock, ErrorNote, FullError, LiveIndicator, Logo29, StreamBanner, useNow, useToast } from '../components/ui';
import {
  IconAlert, IconArrowLeft, IconArrowRight, IconBell, IconBolt, IconCheck, IconChefHat, IconClock, IconFilter,
  IconMaximize, IconMinimize, IconRefresh, IconSettings, IconSoundOff, IconSoundOn, IconUndo,
} from '../components/icons';

const STATION_KEY = 'dining_kds_station';
const DONE_HOURS_KEY = 'dining_kds_done_hours';
const DONE_HOURS = [6, 12, 24];
const DONE_LIMIT = 120;
const PAGE_SECONDS = 10;
const GAP = 8;
const FRESH_MS = 8000;

// The four columns. `band` = the column colour (top band, header dot, button);
// `soft` = the card header tint; `ink` = text on the tint.
const COLUMNS = [
  { key: 'new', title: 'New', band: '#3B82F6', soft: '#E8F0FE', ink: '#1E40AF', tint: 'rgba(59,130,246,.08)', edge: 'rgba(59,130,246,.30)', glow: '#93C5FD',
    btn: { label: 'Start', go: 'start', bg: '#2563EB', fg: '#FFFFFF' }, empty: 'No new orders', icon: IconBell },
  { key: 'preparing', title: 'Cooking', band: '#F59E0B', soft: '#FEF3C7', ink: '#92400E', tint: 'rgba(245,158,11,.08)', edge: 'rgba(245,158,11,.30)', glow: '#FCD34D',
    btn: { label: 'Ready', go: 'ready', bg: '#F59E0B', fg: '#1F1300' }, empty: 'Nothing cooking', icon: IconChefHat },
  { key: 'ready', title: 'Ready', band: '#22C55E', soft: '#DCFCE7', ink: '#166534', tint: 'rgba(34,197,94,.08)', edge: 'rgba(34,197,94,.30)', glow: '#86EFAC',
    btn: { label: 'Done', go: 'serve', bg: '#16A34A', fg: '#FFFFFF' }, empty: 'No orders ready', icon: IconCheck },
  { key: 'done', title: 'Done', band: '#64748B', soft: '#EEF2F6', ink: '#334155', tint: 'rgba(100,116,139,.10)', edge: 'rgba(100,116,139,.34)', glow: '#CBD5E1',
    btn: null, empty: 'No done orders yet', icon: IconClock },
];
const VOID_STYLE = { band: '#DC2626', soft: '#FEE2E2', ink: '#991B1B', btn: { label: 'Acknowledge', go: 'acknowledge', bg: '#DC2626', fg: '#FFFFFF' } };

// Card sizes per density step (0 = roomiest). The last step is the readable minimum.
const DENSITY = [
  { label: 26, item: 19, mod: 14, chip: 12.5, age: 15, btn: 42, pad: 12, line: 4, done: 15 },
  { label: 23, item: 17, mod: 13, chip: 12, age: 14, btn: 38, pad: 10, line: 3, done: 14 },
  { label: 20, item: 15.5, mod: 12.5, chip: 11.5, age: 13, btn: 34, pad: 9, line: 2, done: 13.5 },
  { label: 18, item: 14.5, mod: 12, chip: 11, age: 12.5, btn: 31, pad: 7, line: 1, done: 13 },
];

const CHANNEL = { register: 'Register', kiosk: 'Kiosk', online: 'Online' };
const TAG_BG = { VOIDED: '#DC2626', ADDED: '#2563EB', CHANGED: '#7C3AED' };

// Kitchen age: m:ss under 1 h, "1:05h" under 24 h (never read as minutes), else Nd.
export function kitchenAge(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  if (s < 3600) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  if (s < 86400) return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}h`;
  return `${Math.floor(s / 86400)}d`;
}

function ticketAge(t, now) {
  // age_seconds was read at load; keep it ticking from the sent time
  const sent = parseTime(t.created_at);
  return sent === null ? t.age_seconds || 0 : Math.max(0, (now - sent) / 1000);
}

// Where the food goes, as one small chip: "Dine-in · Minh N.", "Kiosk · Guest",
// "Online · Linh T. · Pickup 8:10 PM", "Register · Takeout · Marco P.".
function whereChip(t) {
  if (isChannelTicket(t)) {
    const parts = [CHANNEL[t.channel] || t.channel];
    if (t.channel === 'register' || t.order_type === 'dine_in') parts.push(orderTypeLabel(t));
    parts.push(shortName(t.customer_name) || 'Guest');
    if (t.pickup_at) parts.push(`Pickup ${clock(t.pickup_at)}`);
    return parts.join(' · ');
  }
  return ['Dine-in', t.round_no > 1 ? `Round ${t.round_no}` : null, shortName(t.server_name) || null].filter(Boolean).join(' · ');
}

function columnOf(t) {
  if (t.status === 'cancelled') return 'new';
  if (t.status === 'preparing') return 'preparing';
  if (t.status === 'ready') return 'ready';
  return 'new';
}

function changeText(changes) {
  if (!changes) return null;
  return Object.entries(changes).map(([field, v]) => {
    const name = field === 'seat_number' ? 'Seat' : field === 'course' ? 'Course' : 'Note';
    return `${name} ${v?.from ?? '—'} → ${v?.to ?? '—'}`;
  }).join(' · ');
}

// what the kitchen printer did with a ticket (ticket.print from the server)
const PRINT_WORDS = {
  printed: { text: 'Printed', color: C.greenText },
  queued: { text: 'Printing…', color: C.textMid },
  printing: { text: 'Printing…', color: C.textMid },
  failed: { text: 'Print failed', color: '#F87171' },
  no_printer: { text: 'No printer', color: C.textLo },
  none: { text: 'Not printed', color: C.textLo },
};
export function printWords(p) {
  return PRINT_WORDS[p?.status] || PRINT_WORDS.none;
}

function MiniTag({ label, z }) {
  return (
    <span style={{ height: Math.round(z.chip * 1.55), padding: '0 5px', borderRadius: 4, background: TAG_BG[label] || C.ink3, color: '#FFFFFF', fontSize: z.chip - 1.5, fontWeight: 800, letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center', flex: 'none' }}>{label}</span>
  );
}

// One item line: "2× Pho Tai (Rare Beef)", then seat / modifiers / note small under,
// an allergy in red, a voided line struck with VOIDED. On a ticket being cooked a
// tap on the line marks that item ready (and back) - a small check, nothing more.
function ItemLine({ t, item, onToggle, busy, canWrite, z, col }) {
  const voided = !!item.voided;
  // an item marked ready on a ticket still cooking; on a Ready ticket every line simply reads
  const done = !voided && (t.status === 'new' || t.status === 'preparing') && (item.status === 'ready' || item.status === 'served');
  const parts = splitNotes(item.notes);
  const sub = [item.seat_number ? `Seat ${item.seat_number}` : null, modifierText(item), parts.note].filter(Boolean).join(' · ');
  const mark = voided ? 'VOIDED' : item.modified_at ? 'CHANGED' : (t.round_no > 1 ? 'ADDED' : null);
  const tappable = canWrite && !voided && (t.status === 'new' || t.status === 'preparing');
  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, lineHeight: 1.22, minWidth: 0 }}>
        <span style={{ fontFamily: FONT_TIGHT, fontSize: z.item, fontWeight: 800, color: voided ? C.redInk : done ? C.faint : col.ink, flex: 'none', minWidth: Math.round(z.item * 1.45) }}>{item.quantity}×</span>
        <span style={{ fontSize: z.item, fontWeight: 700, color: voided ? C.redInk : done ? C.faint : C.ink, textDecoration: voided || done ? 'line-through' : 'none', overflowWrap: 'anywhere', minWidth: 0 }}>{item.name}</span>
        {mark && <MiniTag label={mark} z={z} />}
        {done && <span aria-hidden="true" style={{ color: C.green, flex: 'none', alignSelf: 'center', display: 'inline-flex' }}><IconCheck size={Math.round(z.item * 0.85)} /></span>}
      </div>
      <div style={{ paddingLeft: Math.round(z.item * 1.45) + 6, display: 'flex', flexDirection: 'column' }}>
        {sub && <span style={{ fontSize: z.mod, color: C.muted, lineHeight: 1.3, textDecoration: voided ? 'line-through' : 'none' }}>{sub}</span>}
        {parts.allergy && !voided && (
          <span style={{ fontSize: z.mod, fontWeight: 800, color: '#DC2626', display: 'flex', alignItems: 'center', gap: 4, lineHeight: 1.3 }}>
            <IconAlert size={Math.round(z.mod + 1)} sw={2.4} />ALLERGY · {parts.allergy.toUpperCase()}
          </span>
        )}
        {voided && <span style={{ fontSize: z.mod - 0.5, fontWeight: 700, color: C.redInk, lineHeight: 1.3 }}>Voided {clock(item.voided_at)}{item.void_reason ? ` · ${item.void_reason}` : ''} - do not make</span>}
        {!voided && item.modified_at && <span style={{ fontSize: z.mod - 0.5, fontWeight: 600, color: '#6D28D9', lineHeight: 1.3 }}>Changed {clock(item.modified_at)} · {changeText(item.changes)}</span>}
      </div>
    </>
  );
  const style = { display: 'block', width: '100%', textAlign: 'left', padding: `${z.line + 1}px 0`, border: 'none', background: 'transparent', color: 'inherit', font: 'inherit' };
  if (!tappable) return <div data-line={item.id} style={style}>{body}</div>;
  return (
    <button type="button" data-line={item.id} onClick={() => onToggle(t, item)} disabled={busy} aria-pressed={done}
      aria-label={`${done ? 'Mark not ready' : 'Mark ready'}: ${item.quantity} ${item.name}`} style={{ ...style, cursor: 'pointer' }}>
      {body}
    </button>
  );
}

function AgePill({ age, urgent, z }) {
  const m = age / 60;
  const tone = !urgent || m < 5 ? { bg: 'rgba(15,23,42,.08)', fg: C.ink2 } : m < 10 ? { bg: '#FFEDD5', fg: '#C2410C' } : { bg: '#DC2626', fg: '#FFFFFF' };
  return (
    <span aria-label={`Age ${elapsed(age)}`} style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, height: Math.round(z.age * 1.75), padding: '0 8px', borderRadius: 999, background: tone.bg, color: tone.fg, fontFamily: FONT_TIGHT, fontSize: z.age, fontWeight: 800, letterSpacing: '-0.2px' }}>
      <IconClock size={Math.round(z.age * 0.95)} sw={2.4} />{kitchenAge(age)}
    </span>
  );
}

// An open ticket (New / Cooking / Ready): coloured band, big label, where-chip,
// age, the item lines and ONE full-width button in the column colour.
function TicketCard({ t, now, z, col, canWrite, withStation, busy, fresh, onAction, onItem, maxHeight, clipped }) {
  const cancelled = t.status === 'cancelled';
  const look = cancelled ? { ...col, ...VOID_STYLE } : col;
  const age = ticketAge(t, now);
  const label = ticketPlace(t);
  const groups = [];
  t.items.forEach((item) => {
    const key = item.course || '';
    let g = groups.find((x) => x.key === key);
    if (!g) { g = { key, items: [] }; groups.push(g); }
    g.items.push(item);
  });
  groups.sort((a, b) => String(a.key || '0').localeCompare(String(b.key || '0'), undefined, { numeric: true }));
  const live = t.items.filter((i) => !i.voided);
  const btn = look.btn;
  return (
    <article data-ticket={t.id} data-status={t.status} data-col={col.key} aria-label={`${label} ${cancelled ? 'voided' : col.title}`}
      className={fresh ? (cancelled ? 'kds-fresh-void' : 'kds-fresh') : undefined}
      style={{ background: '#FFFFFF', color: C.ink, borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight,
        boxShadow: t.priority > 0 ? `0 0 0 3px ${C.orange}, 0 2px 8px rgba(0,0,0,.35)` : '0 2px 8px rgba(0,0,0,.35)' }}>
      <div style={{ height: 5, flex: 'none', background: look.band }} />
      <header style={{ flex: 'none', padding: `${Math.round(z.pad * 0.5)}px ${z.pad}px`, background: look.soft, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, whiteSpace: 'nowrap' }}>
        <span style={{ fontFamily: FONT_TIGHT, fontSize: z.label, fontWeight: 800, letterSpacing: '-0.6px', lineHeight: 1.1, color: C.ink, flex: 'none' }}>{label}</span>
        <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.2, flex: '0 1 auto' }}>
          <span title={whereChip(t)} style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: z.chip, fontWeight: 700, color: look.ink }}>{whereChip(t)}</span>
          {withStation && t.station?.name && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: z.chip - 1.5, fontWeight: 700, color: C.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t.station.name}</span>}
        </span>
        <span style={{ flex: 1 }} />
        {t.priority > 0 && <span aria-label="Rush" style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 6, background: C.ink, color: '#FDBA74', fontSize: z.chip - 1, fontWeight: 800 }}><IconBolt size={z.chip} />RUSH</span>}
        {cancelled
          ? <span style={{ flex: 'none', padding: '3px 8px', borderRadius: 6, background: '#DC2626', color: '#FFFFFF', fontSize: z.chip, fontWeight: 800, letterSpacing: '0.05em' }}>VOIDED</span>
          : <AgePill age={age} urgent={t.status !== 'ready'} z={z} />}
      </header>
      <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', padding: `${Math.round(z.pad * 0.35)}px ${z.pad}px 0`, display: 'flex', flexDirection: 'column' }}>
        {groups.map((g) => (
          <div key={g.key || 'none'}>
            {(groups.length > 1 || g.key) && <div style={{ paddingTop: z.line + 2, fontSize: z.chip - 1.5, fontWeight: 800, letterSpacing: '0.1em', color: C.faint, textTransform: 'uppercase' }}>{g.key ? courseLabel(g.key) : 'No course'}</div>}
            {g.items.map((item) => <ItemLine key={item.id} t={t} item={item} onToggle={onItem} busy={busy} canWrite={canWrite} z={z} col={look} />)}
          </div>
        ))}
        {isChannelTicket(t) && t.order_note && <div style={{ margin: `${z.line + 2}px 0 0`, padding: '3px 8px', borderRadius: 6, background: C.sand, color: C.ink2, fontSize: z.mod, fontWeight: 600, lineHeight: 1.3 }}>Note · {t.order_note}</div>}
        {cancelled && <div style={{ margin: `${z.line + 2}px 0 0`, fontSize: z.mod, color: C.redInk, fontWeight: 800, lineHeight: 1.3 }}>Every item was voided after it was sent - stop cooking.</div>}
        {!live.length && !cancelled && <div style={{ padding: `${z.line}px 0`, color: C.muted, fontSize: z.mod }}>No live items.</div>}
      </div>
      {clipped && <div style={{ flex: 'none', padding: `2px ${z.pad}px`, fontSize: z.chip, fontWeight: 800, color: C.orangeInk, background: C.orangeTint }}>Continues · {plural(live.length, 'item')} on this ticket</div>}
      <div style={{ flex: 'none', padding: `${Math.round(z.pad * 0.6)}px ${z.pad}px ${Math.round(z.pad * 0.8)}px` }}>
        {canWrite && btn ? (
          <button type="button" className="c29-press" onClick={() => onAction(t, btn.go)} disabled={busy}
            style={{ width: '100%', height: z.btn, border: 'none', borderRadius: 9, background: btn.bg, color: btn.fg, fontSize: Math.round(z.btn * 0.42), fontWeight: 800, letterSpacing: '0.01em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            {busy ? '…' : btn.label}
          </button>
        ) : <div style={{ fontSize: z.chip, color: C.muted, padding: '2px 0' }}>Sent {clock(t.created_at)}</div>}
      </div>
    </article>
  );
}

// A done ticket: one compact line-and-a-half - label, where, when, who - and Recall.
function DoneCard({ t, z, col, canWrite, withStation, busy, confirming, onRecall }) {
  const voided = t.status === 'cancelled';
  const live = t.items.filter((i) => !i.voided);
  const count = live.reduce((n, i) => n + (i.quantity || 1), 0);
  const summary = voided ? 'Voided after it was sent' : `${plural(count, 'item')} · ${live.map((i) => i.name).join(', ')}`;
  const doneAt = t.done_at || t.served_at || t.acknowledged_at;
  const meta = [withStation ? t.station?.name : null, whereChip(t)].filter(Boolean).join(' · ');
  const canRecall = canWrite && !voided;
  return (
    <article data-ticket={t.id} data-status={t.status} data-col="done" aria-label={`${ticketPlace(t)} done`}
      style={{ background: '#F8FAFC', color: C.ink, borderRadius: 10, overflow: 'hidden', display: 'flex', alignItems: 'stretch', boxShadow: '0 1px 4px rgba(0,0,0,.3)' }}>
      <div style={{ width: 5, flex: 'none', background: voided ? '#DC2626' : col.band }} />
      <div style={{ flex: 1, minWidth: 0, padding: `${Math.round(z.pad * 0.45)}px ${Math.round(z.pad * 0.8)}px`, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap', minWidth: 0 }}>
          <span style={{ fontFamily: FONT_TIGHT, fontSize: z.done + 3, fontWeight: 800, letterSpacing: '-0.4px', flex: 'none' }}>{ticketPlace(t)}</span>
          {voided && <span style={{ flex: 'none', padding: '1px 5px', borderRadius: 4, background: '#FEE2E2', color: C.redInk, fontSize: z.chip - 2, fontWeight: 800, letterSpacing: '0.05em' }}>VOIDED</span>}
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: z.chip - 0.5, fontWeight: 600, color: C.muted }}>{meta}</span>
          <span style={{ flex: 1 }} />
          <span style={{ flex: 'none', fontFamily: FONT_TIGHT, fontSize: z.done - 0.5, fontWeight: 800, color: col.ink }}>{clock(doneAt)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap', minWidth: 0 }}>
          <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: z.done - 1.5, color: voided ? C.redInk : C.ink3, fontWeight: 600 }}>{summary}</span>
          {t.done_by && <span style={{ flex: 'none', fontSize: z.chip - 1, color: C.muted, fontWeight: 600 }}>by {shortName(t.done_by)}</span>}
        </div>
      </div>
      {canRecall && (
        <button type="button" className="c29-press" onClick={() => onRecall(t)} disabled={busy}
          aria-label={confirming ? `Confirm recall ${ticketPlace(t)} to Ready` : `Recall ${ticketPlace(t)} to Ready`}
          style={{ flex: 'none', alignSelf: 'center', margin: `0 ${Math.round(z.pad * 0.6)}px 0 0`, height: Math.round(z.btn * 0.82), padding: confirming ? '0 10px' : 0, width: confirming ? 'auto' : Math.round(z.btn * 0.82),
            borderRadius: 8, border: confirming ? 'none' : `1px solid ${C.border}`, background: confirming ? '#16A34A' : '#FFFFFF', color: confirming ? '#FFFFFF' : C.ink2,
            fontSize: z.chip, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, whiteSpace: 'nowrap' }}>
          <IconUndo size={Math.round(z.chip + 4)} />{confirming ? 'Back to Ready?' : null}
        </button>
      )}
    </article>
  );
}

// ------------------------------------------------------------------ fitting a column

function useBoxSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => setSize((old) => {
      const w = Math.floor(el.clientWidth);
      const h = Math.floor(el.clientHeight);
      return old.w === w && old.h === h ? old : { w, h };
    });
    read();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      return () => window.removeEventListener('resize', read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

function useFontsReady() {
  const [ready, setReady] = useState(0);
  useEffect(() => {
    let alive = true;
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (alive) setReady((n) => n + 1); });
    return () => { alive = false; };
  }, []);
  return ready;
}

// Cards top to bottom in list order; a card that does not fit starts the next
// page. A card taller than the whole column shows what fits ("Continues").
function pack(ids, heights, boxH) {
  const pages = [];
  let page = [];
  let used = 0;
  ids.forEach((id, i) => {
    const full = heights[i] || 0;
    const h = Math.min(full, boxH);
    if (page.length && used + h > boxH) {
      pages.push(page);
      page = [];
      used = 0;
    }
    page.push({ id, clipped: full > boxH });
    used += h + GAP;
  });
  if (page.length) pages.push(page);
  return pages;
}

// One column's body: the cards at the roomiest density that fits, else the densest
// and pages that turn every 10 s. Reports its page state to the column header.
function FitColumn({ col, items, renderCard, signature, onPager, maxD = DENSITY.length - 1 }) {
  const boxRef = useRef(null);
  const measureRef = useRef(null);
  const { w, h } = useBoxSize(boxRef);
  const fonts = useFontsReady();
  const key = `${signature}|${w}x${h}|${fonts}|${maxD}`;
  const [trial, setTrial] = useState({ key: null, d: 0 });
  const [layout, setLayout] = useState(null);
  const trialD = trial.key === key ? trial.d : 0;
  const measuring = w > 0 && h > 0 && items.length > 0 && (!layout || layout.key !== key);

  useLayoutEffect(() => {
    if (!measuring || !measureRef.current) return;
    const heights = Array.from(measureRef.current.children).map((el) => el.offsetHeight);
    const pages = pack(items.map((t) => t.id), heights, h);
    if (pages.length > 1 && trialD < maxD) setTrial({ key, d: trialD + 1 });
    else setLayout({ key, d: trialD, pages });
  }, [measuring, h, trialD, maxD, items, key]);

  const pageCount = layout && items.length ? Math.max(1, layout.pages.length) : 1;
  const [page, setPage] = useState(0);
  const [turn, setTurn] = useState(0); // a manual turn restarts the 10 s timer
  const current = Math.min(page, pageCount - 1);
  useEffect(() => {
    if (pageCount < 2) return undefined;
    const timer = setInterval(() => setPage((p) => (Math.min(p, pageCount - 1) + 1) % pageCount), PAGE_SECONDS * 1000);
    return () => clearInterval(timer);
  }, [pageCount, turn]);
  const go = useCallback((delta) => {
    setPage((p) => (Math.min(p, pageCount - 1) + delta + pageCount) % pageCount);
    setTurn((n) => n + 1);
  }, [pageCount]);

  const byId = useMemo(() => new Map(items.map((t) => [t.id, t])), [items]);
  const shown = layout ? (layout.pages[current] || []).filter((p) => byId.has(p.id)) : [];
  const measured = !!layout;
  useEffect(() => { onPager(col.key, { page: current, pages: pageCount, shown: shown.length, measured, go }); },
    [col.key, current, pageCount, shown.length, measured, go, onPager]);

  const Icon = col.icon;
  return (
    <div ref={boxRef} data-testid={`kds-body-${col.key}`} style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {measuring && (
        <div ref={measureRef} aria-hidden="true" inert="" style={{ position: 'absolute', left: 0, top: 0, width: w, visibility: 'hidden', pointerEvents: 'none', display: 'flex', flexDirection: 'column' }}>
          {items.map((t) => <div key={t.id}>{renderCard(t, DENSITY[trialD], {})}</div>)}
        </div>
      )}
      {!items.length && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: C.textOff }}>
          <Icon size={34} />
          <span style={{ fontSize: 15, fontWeight: 600 }}>{col.empty}</span>
        </div>
      )}
      {layout && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: GAP }}>
          {shown.map((p) => <div key={p.id}>{renderCard(byId.get(p.id), DENSITY[layout.d], { maxHeight: h, clipped: p.clipped })}</div>)}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ small header parts

function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);
  return online;
}

function useViewportHeight() {
  const [vh, setVh] = useState(() => (typeof window === 'undefined' ? 1080 : window.innerHeight));
  useEffect(() => {
    const read = () => setVh(window.innerHeight);
    window.addEventListener('resize', read);
    return () => window.removeEventListener('resize', read);
  }, []);
  return vh;
}

function useFullscreen() {
  const [on, setOn] = useState(() => typeof document !== 'undefined' && !!document.fullscreenElement);
  useEffect(() => {
    const read = () => setOn(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', read);
    return () => document.removeEventListener('fullscreenchange', read);
  }, []);
  const toggle = useCallback(() => {
    try {
      if (document.fullscreenElement) document.exitFullscreen();
      else if (document.documentElement.requestFullscreen) document.documentElement.requestFullscreen();
    } catch (e) { /* the browser said no */ }
  }, []);
  const can = typeof document !== 'undefined' && !!document.documentElement.requestFullscreen;
  return [on, toggle, can];
}

const iconBtn = (on) => ({
  width: 42, height: 42, borderRadius: 11, border: `1px solid ${on ? 'rgba(249,115,22,.55)' : C.line2}`, background: on ? 'rgba(249,115,22,.14)' : C.panel2,
  color: on ? '#FDBA74' : C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flex: 'none', position: 'relative',
});

// A small menu under a header icon (station filter, settings); closes on an outside tap.
function Popover({ open, onClose, children, width = 260, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => { if (ref.current && !ref.current.parentElement.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', down); document.removeEventListener('keydown', key); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div ref={ref} role="menu" aria-label={label} className="c29-fade"
      style={{ position: 'absolute', top: 50, right: 0, zIndex: 40, width, padding: 8, borderRadius: 14, background: C.panel, border: `1px solid ${C.line2}`, boxShadow: '0 18px 40px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', gap: 4 }}>
      {children}
    </div>
  );
}

const menuRow = (on) => ({
  height: 44, padding: '0 12px', borderRadius: 10, border: 'none', background: on ? 'rgba(249,115,22,.16)' : 'transparent', color: on ? '#FDBA74' : C.text,
  fontSize: 15, fontWeight: on ? 800 : 600, display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%',
});

// ------------------------------------------------------------------ the screen

export default function KDSScreen({ accountMenu = null }) {
  const { me, perms } = useDiningSession();
  const toast = useToast();
  const now = useNow(1000);
  const online = useOnline();
  const vh = useViewportHeight();
  const roomy = vh >= 900;
  const [params, setParams] = useSearchParams();
  const [soundOn, toggleSound] = useSoundPref('dining_kds_sound');
  const [fullscreen, toggleFullscreen, canFullscreen] = useFullscreen();
  const stored = (() => { try { return localStorage.getItem(STATION_KEY); } catch (e) { return null; } })();
  const stationParam = params.get('station') ?? stored ?? '';
  const stationId = stationParam && stationParam !== 'all' ? Number(stationParam) : null;
  const [doneHours, setDoneHours] = useState(() => {
    try { const n = Number(localStorage.getItem(DONE_HOURS_KEY)); return DONE_HOURS.includes(n) ? n : 12; } catch (e) { return 12; }
  });
  const stations = useLive(() => api.get('/dining/kitchen/stations'), [], { types: ['station.changed', 'station.routes_changed'] });
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;
  const board = useLive(async () => {
    const [active, done] = await Promise.all([
      api.get('/dining/kitchen/tickets', { view: 'active', limit: 300 }),
      api.get('/dining/kitchen/tickets', { view: 'done', limit: DONE_LIMIT, since_hours: doneHours, station_id: stationId ?? undefined }),
    ]);
    return { active: active.tickets, done: done.tickets, doneCount: done.done_count ?? done.tickets.length, stale: active.stale_count || 0, staleHours: active.stale_after_hours };
  }, [stationId, doneHours], {
    match: (e) => e.type.startsWith('ticket.') || e.type === 'order.sent' || e.type === 'item.voided' || e.type === 'item.updated' || e.type.startsWith('session.'),
    onEvent: (e) => {
      if (!soundRef.current) return;
      const mine = (tk) => stationId === null || tk?.station_id === stationId || tk?.station?.id === stationId;
      if (e.type === 'order.sent' && (e.payload?.tickets || []).some(mine)) chime('new');
      if (e.type === 'ticket.cancelled' && mine(e.payload?.ticket)) chime('alert');
    },
  });
  const [busy, setBusy] = useState(null);
  const [pagers, setPagers] = useState({});
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmRecall, setConfirmRecall] = useState(null);
  const [menu, setMenu] = useState(null); // 'station' | 'settings'
  const canWrite = perms.can('kds.write');
  const canClear = perms.can('kitchen.config');

  useEffect(() => {
    if (!confirmClear) return undefined;
    const t = setTimeout(() => setConfirmClear(false), 5000);
    return () => clearTimeout(t);
  }, [confirmClear]);
  useEffect(() => {
    if (!confirmRecall) return undefined;
    const t = setTimeout(() => setConfirmRecall(null), 4000);
    return () => clearTimeout(t);
  }, [confirmRecall]);

  const chooseStation = (id) => {
    const next = new URLSearchParams(params);
    next.set('station', id === null ? 'all' : String(id));
    setParams(next, { replace: true });
    try { localStorage.setItem(STATION_KEY, id === null ? 'all' : String(id)); } catch (e) { /* ignore */ }
    setMenu(null);
  };
  const chooseDoneHours = (n) => {
    setDoneHours(n);
    try { localStorage.setItem(DONE_HOURS_KEY, String(n)); } catch (e) { /* ignore */ }
  };

  const active = board.data?.active;
  const all = useMemo(() => active || [], [active]);
  const tickets = useMemo(() => all.filter((t) => stationId === null || t.station?.id === stationId), [all, stationId]);
  const done = useMemo(() => board.data?.done || [], [board.data]);
  const byColumn = useMemo(() => {
    const out = { new: [], preparing: [], ready: [], done };
    tickets.forEach((t) => out[columnOf(t)].push(t));
    // a voided-after-send ticket heads New until the kitchen acknowledges it
    out.new.sort((a, b) => (a.status === 'cancelled' ? 0 : 1) - (b.status === 'cancelled' ? 0 : 1));
    return out;
  }, [tickets, done]);
  const counts = {
    new: byColumn.new.length, preparing: byColumn.preparing.length, ready: byColumn.ready.length,
    done: Math.max(board.data?.doneCount || 0, done.length),
  };
  const perStation = {};
  all.forEach((t) => { perStation[t.station?.id] = (perStation[t.station?.id] || 0) + 1; });
  const stationList = (stations.data?.stations || []).filter((s) => s.is_active !== false);
  const station = stationList.find((s) => s.id === stationId) || (stations.data?.stations || []).find((s) => s.id === stationId);
  const liveStatus = online ? board.streamStatus : STATUS.STOPPED;

  // a new ticket flashes for a few seconds (the first load flashes nothing)
  const seen = useRef(null);
  const [fresh, setFresh] = useState({});
  useEffect(() => {
    if (!board.data) return undefined;
    const ids = tickets.map((t) => t.id);
    if (seen.current === null) { seen.current = new Set(ids); return undefined; }
    const added = ids.filter((id) => !seen.current.has(id));
    ids.forEach((id) => seen.current.add(id));
    if (!added.length) return undefined;
    setFresh((old) => { const next = { ...old }; added.forEach((id) => { next[id] = true; }); return next; });
    const timer = setTimeout(() => setFresh((old) => { const next = { ...old }; added.forEach((id) => { delete next[id]; }); return next; }), FRESH_MS);
    return () => clearTimeout(timer);
  }, [tickets, board.data]);

  const act = useCallback(async (t, action) => {
    setBusy(t.id);
    try {
      await api.post(`/dining/kitchen/tickets/${t.id}/${action}`, { expected_version: t.version }, { idempotencyKey: newKey(`kds-${action}`) });
    } catch (e) {
      toast.error(e.isStale ? 'Another screen changed this ticket - showing the latest.' : errorText(e));
    } finally {
      setBusy(null);
      board.reload();
    }
  }, [board, toast]);

  const toggleItem = useCallback(async (t, item) => {
    const action = item.status === 'ready' || item.status === 'served' ? 'recall' : 'ready';
    setBusy(t.id);
    try {
      await api.post(`/dining/kitchen/tickets/${t.id}/items/${item.id}/${action}`, { expected_version: t.version }, { idempotencyKey: newKey(`kds-item-${action}`) });
    } catch (e) {
      toast.error(e.isStale ? 'Another screen changed this ticket - showing the latest.' : errorText(e));
    } finally {
      setBusy(null);
      board.reload();
    }
  }, [board, toast]);

  const recall = useCallback(async (t) => {
    if (confirmRecall !== t.id) { setConfirmRecall(t.id); return; }
    setConfirmRecall(null);
    setBusy(t.id);
    try {
      await api.post(`/dining/kitchen/tickets/${t.id}/recall`, { expected_version: t.version }, { idempotencyKey: newKey('kds-recall') });
      toast.ok(`${ticketPlace(t)} is back in Ready`);
    } catch (e) {
      toast.error(e.isStale ? 'Another screen changed this ticket - showing the latest.' : errorText(e));
    } finally {
      setBusy(null);
      board.reload();
    }
  }, [board, toast, confirmRecall]);

  const reprint = useCallback(async (t) => {
    setBusy(t.id);
    try {
      await api.post(`/dining/kitchen/tickets/${t.id}/reprint`, {}, { idempotencyKey: newKey('kds-reprint') });
      toast.ok(`${ticketPlace(t)} sent to the printer`);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
      board.reload();
    }
  }, [board, toast]);

  // the latest tickets of this station for Reprint (Settings), newest first
  const printable = useMemo(() => [...tickets, ...done]
    .filter((t) => t.status !== 'cancelled')
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
    .slice(0, 6), [tickets, done]);
  const printFailed = tickets.some((t) => t.print?.status === 'failed');

  const clearOld = async () => {
    if (!confirmClear) { setConfirmClear(true); return; }
    setConfirmClear(false);
    try {
      const answer = await api.post('/dining/kitchen/tickets/clear-old', {}, { idempotencyKey: newKey('kds-clear-old') });
      toast.ok(answer?.cleared ? `${plural(answer.cleared, 'old ticket')} closed` : 'No old tickets to close');
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      board.reload();
    }
  };

  const onPager = useCallback((key, p) => setPagers((old) => {
    const o = old[key];
    return o && o.page === p.page && o.pages === p.pages && o.shown === p.shown && o.measured === p.measured && o.go === p.go ? old : { ...old, [key]: p };
  }), []);

  const withStation = stationId === null;
  const colByKey = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));
  const renderers = {
    new: (t, z, extra) => <TicketCard t={t} now={now} z={z} col={colByKey.new} canWrite={canWrite} withStation={withStation} busy={busy === t.id} fresh={!!fresh[t.id]} onAction={act} onItem={toggleItem} {...extra} />,
    preparing: (t, z, extra) => <TicketCard t={t} now={now} z={z} col={colByKey.preparing} canWrite={canWrite} withStation={withStation} busy={busy === t.id} fresh={!!fresh[t.id]} onAction={act} onItem={toggleItem} {...extra} />,
    ready: (t, z, extra) => <TicketCard t={t} now={now} z={z} col={colByKey.ready} canWrite={canWrite} withStation={withStation} busy={busy === t.id} fresh={!!fresh[t.id]} onAction={act} onItem={toggleItem} {...extra} />,
    done: (t, z) => <DoneCard t={t} z={z} col={colByKey.done} canWrite={canWrite} withStation={withStation} busy={busy === t.id} confirming={confirmRecall === t.id} onRecall={recall} />,
  };
  const signatureOf = (list) => `${list.map((t) => `${t.id}.${t.version}.${t.items.length}.${t.status}`).join(',')}|${canWrite ? 1 : 0}|${withStation ? 1 : 0}`;

  const counterTile = (c) => (
    <div key={c.key} data-count={counts[c.key]} data-col={c.key}
      style={{ flex: 1, minWidth: 0, height: roomy ? 78 : 58, borderRadius: 14, background: c.tint, border: `1px solid ${c.edge}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: roomy ? 14 : 10, position: 'relative', overflow: 'hidden' }}>
      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, background: c.band }} />
      <span style={{ fontFamily: FONT_TIGHT, fontSize: roomy ? 44 : 32, fontWeight: 800, letterSpacing: '-1px', color: c.glow, lineHeight: 1 }}>{counts[c.key]}</span>
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
        <span style={{ fontSize: roomy ? 17 : 14, fontWeight: 800, color: C.textHi, letterSpacing: '0.02em' }}>{c.title}</span>
        {c.key === 'done' && <span style={{ fontSize: roomy ? 12.5 : 11.5, color: C.textLo, fontWeight: 600 }}>last {doneHours} h</span>}
      </span>
    </div>
  );

  return (
    <div className="c29 c29-dark" data-testid="kds-screen" style={{ height: '100vh', maxHeight: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.floor }}>
      <header style={{ position: 'relative', zIndex: 30, height: roomy ? 68 : 60, flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', background: C.bar, borderBottom: `1px solid ${C.line}`, minWidth: 0 }}>
        <Logo29 size={roomy ? 40 : 36} />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0, flex: '0 1 auto' }}>
          <span style={{ fontFamily: FONT_TIGHT, fontSize: roomy ? 21 : 18, fontWeight: 800, color: C.textHi, whiteSpace: 'nowrap', letterSpacing: '-0.3px' }}>Kitchen Display</span>
          <span data-testid="kds-where" style={{ fontSize: 12.5, color: C.textLo, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>{[me?.restaurant_name, station?.name || 'All stations'].filter(Boolean).join(' · ')}</span>
        </div>
        <span data-testid="kds-live" style={{ flex: 'none', height: 32, padding: '0 12px', borderRadius: 999, border: `1px solid ${C.line2}`, background: C.panel2, display: 'inline-flex', alignItems: 'center' }}>
          <LiveIndicator status={liveStatus} size={13} />
        </span>
        {!canWrite && <span style={{ flex: 'none', fontSize: 12.5, fontWeight: 700, color: C.textLo }}>View only</span>}
        <div style={{ flex: 1, minWidth: 0 }} />
        {board.data?.stale > 0 && (
          <div data-testid="kds-stale" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none', fontSize: 13, color: C.textMid, whiteSpace: 'nowrap' }}>
            <span>{plural(board.data.stale, 'old ticket')} hidden</span>
            {canClear && (
              <button type="button" className="c29-press" onClick={clearOld}
                style={{ height: 34, padding: '0 12px', borderRadius: 9, border: `1px solid ${confirmClear ? C.red : C.line2}`, background: confirmClear ? C.red : C.panel2, color: '#FFFFFF', fontSize: 13, fontWeight: 700, flex: 'none' }}>
                {confirmClear ? `Close ${board.data.stale} - tap again` : 'Clear old tickets'}
              </button>
            )}
          </div>
        )}
        <Clock size={roomy ? 28 : 24} color={C.textHi} weight={800} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
          <button type="button" onClick={toggleSound} aria-pressed={!soundOn} aria-label={soundOn ? 'Sound on' : 'Muted'} title={soundOn ? 'New-order chime on' : 'Muted'} className="c29-press" style={{ ...iconBtn(false), color: soundOn ? C.greenText : C.textOff }}>
            {soundOn ? <IconSoundOn size={20} /> : <IconSoundOff size={20} />}
          </button>
          <button type="button" onClick={() => board.reload()} aria-label="Refresh" title="Refresh" className="c29-press" style={iconBtn(false)}>
            <IconRefresh size={19} />
          </button>
          <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => setMenu(menu === 'station' ? null : 'station')} aria-haspopup="menu" aria-expanded={menu === 'station'}
              aria-label={`Station filter: ${station?.name || 'All stations'}`} title="Station" className="c29-press" style={iconBtn(stationId !== null)}>
              <IconFilter size={19} />
              {stationId !== null && <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: '50%', background: C.orange }} />}
            </button>
            <Popover open={menu === 'station'} onClose={() => setMenu(null)} label="Station">
              <div style={{ padding: '4px 10px 6px', fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', color: C.textLo, textTransform: 'uppercase' }}>Show tickets for</div>
              <button type="button" role="menuitemradio" aria-checked={stationId === null} onClick={() => chooseStation(null)} style={menuRow(stationId === null)}>
                <span style={{ flex: 1 }}>All stations</span><span style={{ color: C.textLo, fontWeight: 700 }}>{all.length}</span>
              </button>
              {stationList.map((s) => (
                <button key={s.id} type="button" role="menuitemradio" aria-checked={s.id === stationId} onClick={() => chooseStation(s.id)} style={menuRow(s.id === stationId)}>
                  <span style={{ flex: 1 }}>{s.name}</span><span style={{ color: C.textLo, fontWeight: 700 }}>{perStation[s.id] || 0}</span>
                </button>
              ))}
            </Popover>
          </div>
          {canFullscreen && (
            <button type="button" onClick={toggleFullscreen} aria-label={fullscreen ? 'Exit full screen' : 'Full screen'} title={fullscreen ? 'Exit full screen' : 'Full screen'} className="c29-press" style={iconBtn(false)}>
              {fullscreen ? <IconMinimize size={19} /> : <IconMaximize size={19} />}
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => setMenu(menu === 'settings' ? null : 'settings')} aria-haspopup="menu" aria-expanded={menu === 'settings'} aria-label="Settings" title="Settings" className="c29-press" style={iconBtn(menu === 'settings')}>
              <IconSettings size={19} />
              {printFailed && <span data-testid="kds-print-failed" aria-label="A ticket did not print" style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: 4, background: '#F87171' }} />}
            </button>
            <Popover open={menu === 'settings'} onClose={() => setMenu(null)} label="Settings" width={280}>
              <div style={{ padding: '4px 10px 6px', fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', color: C.textLo, textTransform: 'uppercase' }}>Done column shows</div>
              {DONE_HOURS.map((n) => (
                <button key={n} type="button" role="menuitemradio" aria-checked={doneHours === n} onClick={() => chooseDoneHours(n)} style={menuRow(doneHours === n)}>
                  <span style={{ flex: 1 }}>Last {n} hours</span>{doneHours === n && <IconCheck size={16} />}
                </button>
              ))}
              <div style={{ height: 1, background: C.line2, margin: '4px 6px' }} />
              <button type="button" role="menuitemcheckbox" aria-checked={soundOn} onClick={toggleSound} style={menuRow(false)}>
                <span style={{ flex: 1 }}>New-order chime</span><span style={{ color: soundOn ? C.greenText : C.textLo, fontWeight: 800 }}>{soundOn ? 'On' : 'Off'}</span>
              </button>
              {canWrite && printable.length > 0 && (
                <>
                  <div style={{ height: 1, background: C.line2, margin: '4px 6px' }} />
                  <div style={{ padding: '4px 10px 6px', fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', color: C.textLo, textTransform: 'uppercase' }}>Kitchen printer</div>
                  {printable.map((t) => {
                    const words = printWords(t.print);
                    const noPrinter = t.print?.status === 'no_printer';
                    return (
                      <button key={t.id} type="button" role="menuitem" data-testid="kds-reprint" data-ticket={t.id}
                        disabled={busy === t.id || noPrinter} onClick={() => reprint(t)}
                        title={t.print?.last_error || t.print?.message || undefined}
                        aria-label={noPrinter ? `${ticketPlace(t)}: no printer set up` : `Reprint ${ticketPlace(t)}`}
                        style={{ ...menuRow(false), opacity: noPrinter ? 0.6 : 1 }}>
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {noPrinter ? '' : 'Reprint '}{ticketPlace(t)}{withStation && t.station?.name ? ` · ${t.station.name}` : ''}
                        </span>
                        <span style={{ color: words.color, fontWeight: 700, fontSize: 12.5, whiteSpace: 'nowrap' }}>{busy === t.id ? '…' : words.text}</span>
                      </button>
                    );
                  })}
                  {printable.some((t) => t.print?.status === 'no_printer') && (
                    <div style={{ padding: '4px 10px 6px', fontSize: 12, color: C.textLo, lineHeight: 1.35 }}>
                      No printer is routed to this station: Back Office → Kitchen → Print Routing (station code = the station's code).
                    </div>
                  )}
                </>
              )}
            </Popover>
          </div>
          {accountMenu || <UserMenu me={me} size={40} />}
        </div>
      </header>
      <StreamBanner status={liveStatus} onRetry={board.reload} />

      <main style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: roomy ? 12 : 10, padding: roomy ? '12px 16px 14px' : '10px 12px 12px' }}>
        <div data-testid="kds-counters" style={{ display: 'flex', gap: roomy ? 12 : 10, flex: 'none' }}>
          {COLUMNS.map(counterTile)}
        </div>
        {board.error && board.data && <ErrorNote error={`Showing the last board - ${errorText(board.error)}`} onRetry={board.reload} compact style={{ flex: 'none' }} />}
        {board.loading && !board.data && <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textLo, fontSize: 15 }}>Loading the tickets…</div>}
        {board.error && !board.data && <FullError error={board.error} onRetry={board.reload} title="Could not load the tickets" />}
        {board.data && (
          <div data-testid="kds-board" style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gridTemplateRows: 'minmax(0, 1fr)', overflow: 'hidden', gap: roomy ? 12 : 10 }}>
            {COLUMNS.map((c) => {
              const items = byColumn[c.key];
              const p = pagers[c.key];
              // cards on other pages, and (Done) the history past what was loaded
              const more = p && p.measured && items.length ? Math.max(0, counts[c.key] - p.shown) : 0;
              return (
                <section key={c.key} data-testid={`kds-col-${c.key}`} aria-label={`${c.title} ${counts[c.key]}`}
                  style={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', borderRadius: 16, background: c.tint, border: `1px solid ${c.edge}`, overflow: 'hidden' }}>
                  <div style={{ height: 5, flex: 'none', background: c.band }} />
                  <div style={{ height: roomy ? 46 : 40, flex: 'none', display: 'flex', alignItems: 'center', gap: 9, padding: '0 12px', minWidth: 0 }}>
                    <span style={{ width: 12, height: 12, borderRadius: '50%', background: c.band, flex: 'none', boxShadow: `0 0 0 4px ${c.tint}` }} />
                    <span style={{ fontFamily: FONT_TIGHT, fontSize: roomy ? 19 : 16.5, fontWeight: 800, color: C.textHi, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{c.title}</span>
                    <span data-testid={`kds-count-${c.key}`} style={{ minWidth: 28, height: 26, padding: '0 8px', borderRadius: 13, background: c.band, color: c.key === 'preparing' ? '#1F1300' : '#FFFFFF', fontSize: 14, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{counts[c.key]}</span>
                    <span style={{ flex: 1 }} />
                  </div>
                  <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '0 8px 8px' }}>
                    <FitColumn col={c} items={items} renderCard={renderers[c.key]} signature={signatureOf(items) + (c.key === 'done' ? `|${confirmRecall}` : '')} onPager={onPager} maxD={roomy ? 2 : DENSITY.length - 1} />
                  </div>
                  {(more > 0 || (p && p.pages > 1)) && (
                    <div style={{ height: roomy ? 40 : 36, flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px', borderTop: `1px solid ${c.edge}`, background: 'rgba(0,0,0,.18)' }}>
                      {p && p.pages > 1 && <button type="button" aria-label={`${c.title}: previous page`} onClick={() => p.go(-1)} style={{ ...iconBtn(false), width: 30, height: 28, borderRadius: 8 }}><IconArrowLeft size={15} /></button>}
                      <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        {more > 0 && <span data-testid={`kds-more-${c.key}`} style={{ padding: '2px 9px', borderRadius: 999, background: c.band, color: c.key === 'preparing' ? '#1F1300' : '#FFFFFF', fontSize: 12.5, fontWeight: 800 }}>+{more} more</span>}
                        {p && p.pages > 1 && (
                          <span data-testid={`kds-pager-${c.key}`} aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: C.textMid }}>
                            Page {p.page + 1} of {p.pages}
                            <span style={{ display: 'flex', gap: 3 }}>{Array.from({ length: Math.min(p.pages, 8) }, (_, i) => <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: i === p.page ? c.band : C.line2 }} />)}</span>
                          </span>
                        )}
                      </span>
                      {p && p.pages > 1 && <button type="button" aria-label={`${c.title}: next page`} onClick={() => p.go(1)} style={{ ...iconBtn(false), width: 30, height: 28, borderRadius: 8 }}><IconArrowRight size={15} /></button>}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
